import Phaser from 'phaser';
import audioManager from '../AudioManager';
import BotController from '../BotController';
import { BANNER_HEIGHT } from '../AdManager';
import { addCoins, getCoins } from '../CoinManager';
import { getUnlockedAnimals, UNLOCKABLE_ANIMALS } from '../AnimalShopManager';

const BASE_ANIMALS = ['fox', 'tiger', 'rhino', 'eagle', 'lion', 'giraffe', 'elephant', 'cat', 'bear'];
const ALL_ANIMALS = [...BASE_ANIMALS, ...UNLOCKABLE_ANIMALS];
let ANIMALS = [...BASE_ANIMALS];

// ── Base design resolution ────────────────────────────────────────
const BASE_WIDTH = 420;

// Populated in create() once we know the real screen width
let S = 1;

// ── Animal config at base resolution (420px wide) ─────────────────
const BASE_ANIMAL_CONFIG = {
  fox:      { w: 253.125, h: 253.125, density: 1.0, friction: 0.5 },
  tiger:    { w: 281.25, h: 225, density: 1.5, friction: 0.5 },
  rhino:    { w: 309.375, h: 239.0625, density: 2.5, friction: 0.5 },
  eagle:    { w: 281.25, h: 225, density: 1.0, friction: 0.5 },
  lion:     { w: 281.25, h: 253.125, density: 2.3, friction: 0.5 },
  giraffe:  { w: 337.5, h: 365.625, density: 2.0, friction: 0.5 },
  elephant: { w: 365.625, h: 368.4375, density: 3.0, friction: 0.5 },
  cat:      { w: 126.5625,  h: 126.5625,  density: 1.8, friction: 0.5 },
  bear:     { w: 267.1875, h: 267.1875, density: 3.0, friction: 0.5 },
  cow:      { w: 300, h: 240, density: 3.0, friction: 0.5 },
  panda:    { w: 207.1875, h: 207.1875, density: 3.0, friction: 0.5 },
  pig:      { w: 200, h: 200, density: 1.2, friction: 0.5 },
  puppy:    { w: 150, h: 150, density: 1.0, friction: 0.5 },
  horse:    { w: 180, h: 180, density: 1.8, friction: 0.5 },
  tiger2:   { w: 250, h: 200, density: 1.8, friction: 0.5 },
};

const WEB_SCALE = 0.5;
const cfg = (name) => ({
  ...BASE_ANIMAL_CONFIG[name],
  w: BASE_ANIMAL_CONFIG[name].w * S * WEB_SCALE,
  h: BASE_ANIMAL_CONFIG[name].h * S * WEB_SCALE,
});

const PIXEL_SCALE          = 50;
const toR                  = (px) => px / PIXEL_SCALE;
const toPx                 = (r)  => r  * PIXEL_SCALE;
const DEBUG_PHYSICS        = false;
const PLATFORM_WIDTH_RATIO = 0.80;
const ROTATE_STEP          = Phaser.Math.DegToRad(30);

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.RAPIER        = null;
    this.rapierWorld   = null;
    this.animalBodies  = [];
    this.shapes        = null;
    this.scaledShapes  = {};
    this.animalQueue   = [];
    this.currentAnimalName  = null;
    this.previewImage  = null;
    this.previewAngle  = 0;
    this.aimX          = 0;
    this.canDrop       = false;
    this.score         = 0;
    this.gameOver      = false;
    this.groundY       = 0;
    this.platformW     = 0;
    this.platformX     = 0;
    this.spawnAboveStack = 0;
    this.aimLine       = null;
    this.aimArrow      = null;
    this.debugGraphics = null;
    this.maxTowerHeight   = 0;
    this.currentSpawnY    = 0;
    this.fallenCheckTimer = null;
    this.S             = 1;
    this.logicalHeight = 0;
    this.ignoreDrop = false;
    this.pauseContainer = null;
    // Bot mode state
    this.gameMode      = 'solo';
    this.currentTurn   = 'player';
    this.lastDropper   = 'player';
    this.botController = null;
    this.botAnimTween  = null;
    this.playerScore   = 0;
    this.botScore      = 0;
    // Rescue / ad state
    this.rescueUsed         = false;
    this.lastStableSnapshot = null;
    // Rotation counter for ads
    this.rotationsLeft      = 12;
    this.nextAnimalRevealed = false;
  }

  /* ─────────────────────────── INIT ────────────────────────────── */
  init(data) {
    this.gameMode = (data && data.mode === 'vs_bot') ? 'vs_bot' : 'solo';
  }

  /* ─────────────────────────── PRELOAD ─────────────────────────── */
  preload() {
    ALL_ANIMALS.forEach(name => {
      this.load.image(name, `assets/images/${name}.png`);
    });
    this.load.json('shapes', 'assets/shapes.json');
  }

  /* ─────────────────────────── CREATE ──────────────────────────── */
  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    const dpr = window.devicePixelRatio || 1;

    this.S = Math.min((W / dpr) / BASE_WIDTH, 1.8);
    S = Math.min(W / BASE_WIDTH, 1.8);

    ALL_ANIMALS.forEach(name => {
      const texture = this.textures.get(name);
      texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    });

    this.RAPIER = window.__RAPIER__;
    if (!this.RAPIER) { console.error('Rapier WASM not ready.'); return; }

    getUnlockedAnimals().then(unlocked => {
      ANIMALS = [...BASE_ANIMALS, ...unlocked.filter(a => UNLOCKABLE_ANIMALS.includes(a))];
      this.animalQueue = [];
      this.refillQueue();
    });

    const sd = this.S * dpr;
    const bannerOffset = BANNER_HEIGHT * dpr;
    const btnH = 65 * sd;
    const btnBottomPad = 75 * sd;
    this.groundY = H - bannerOffset - btnBottomPad - btnH - 30 * sd;
    this.platformW       = W * PLATFORM_WIDTH_RATIO;
    this.platformX       = W / 2;
    this.spawnAboveStack = 100 * S;

    this.buildWorld();

    this.shapes = this.cache.json.get('shapes');
    ALL_ANIMALS.forEach(name => {
      this.scaledShapes[name] = this.extractScaledPolygons(name);
    });

    if (DEBUG_PHYSICS) this.debugGraphics = this.add.graphics().setDepth(999);

    // ── Input ─────────────────────────────────────────────────────

    // Only move animal when actively dragging (pointer held down)
    this.input.on('pointermove', pointer => {
      if (!this.canDrop || this.gameOver) return;
      if (!pointer.isDown) return;
      // Only move if finger has dragged more than 20px from press point
      const dragDistance = Phaser.Math.Distance.Between(
        pointer.downX, pointer.downY,
        pointer.x, pointer.y
      );
      if (dragDistance < 20) return;
      const half = this.platformW / 2;
      this.aimX = Phaser.Math.Clamp(
        pointer.x,
        this.platformX - half - 20,
        this.platformX + half + 20
      );
    });

    // Tap anywhere above bottom 15% + banner = drop at current position
    const bannerPx = BANNER_HEIGHT * (window.devicePixelRatio || 1);
    this.input.on('pointerup', pointer => {
      if (!this.canDrop || this.gameOver) return;
      if (this.ignoreDrop) return;
      if (this.ignoreRotate) return;
      if (pointer.y > this.scale.height * 0.85 - bannerPx) return; // ignore bottom (button + banner area)
      const dragDistance = Phaser.Math.Distance.Between(
        pointer.downX, pointer.downY,
        pointer.upX,   pointer.upY
      );
      if (dragDistance < 20) this.dropCurrentAnimal(); // only drop on tap, not drag release
    });

    if (this.gameMode === 'vs_bot') {
      this.botController = new BotController(this);
    }

    this.startGame();

    if (!this.scene.isActive('UIScene')) {
      this.scene.launch('UIScene', { mode: this.gameMode });
    }
  }

  /* ─────────────────── BUILD STATIC WORLD ─────────────────────── */
  buildWorld() {
    const W = this.scale.width;
    const H = this.scale.height;

    const skyBg = this.add.graphics().setDepth(0);
    const skyHeight = H * 10; // tall enough to cover any tower height
    skyBg.fillGradientStyle(0x1a8fff, 0x1a8fff, 0x6dd5ff, 0x6dd5ff, 1);
    skyBg.fillRect(0, -skyHeight, W, skyHeight + H);

    this.clouds = [];
    const cloudConfigs = [
      { x: W * 0.15, y: H * 0.08, scale: 1.1 * S, speed: 0.18 * S },
      { x: W * 0.55, y: H * 0.05, scale: 0.8 * S, speed: 0.12 * S },
      { x: W * 0.80, y: H * 0.13, scale: 1.3 * S, speed: 0.22 * S },
      { x: W * 0.35, y: H * 0.18, scale: 0.7 * S, speed: 0.15 * S },
      { x: -W * 0.1, y: H * 0.10, scale: 1.0 * S, speed: 0.20 * S },
    ];
    cloudConfigs.forEach(cc => {
      const cloud = this.add.graphics().setDepth(1);
      this.drawCloud(cloud, 0, 0, cc.scale);
      cloud.x = cc.x;
      cloud.y = cc.y;
      cloud.speedX = cc.speed;
      this.clouds.push(cloud);
    });

    const GROUND_H = Math.max(20, 28 * S);

    const grass = this.add.graphics().setDepth(2);
    grass.fillStyle(0x4caf50);
    grass.fillRect(this.platformX - this.platformW / 2, this.groundY - 6 * S, this.platformW, 10 * S);
    grass.fillStyle(0x66bb6a);
    grass.fillRect(this.platformX - this.platformW / 2 - 4 * S, this.groundY - 6 * S, 8 * S, 10 * S);
    grass.fillRect(this.platformX + this.platformW / 2 - 4 * S, this.groundY - 6 * S, 8 * S, 10 * S);

    void GROUND_H;

    this.aimLine = this.add.graphics().setDepth(4);
  }

  drawCloud(gfx, x, y, scale) {
    gfx.fillStyle(0xffffff, 0.92);
    const blobs = [
      { ox: 0,   oy: 0,   r: 28 },
      { ox: 28,  oy: -8,  r: 22 },
      { ox: -28, oy: -6,  r: 20 },
      { ox: 14,  oy: -20, r: 18 },
      { ox: -12, oy: -18, r: 16 },
      { ox: 42,  oy: 4,   r: 16 },
      { ox: -42, oy: 4,   r: 15 },
    ];
    blobs.forEach(b => {
      gfx.fillCircle(x + b.ox * scale, y + b.oy * scale, b.r * scale);
    });
  }

  /* ────────────── START / RESTART GAME STATE ───────────────────── */
  startGame() {
    this.ignoreRotate = true;
    this.time.delayedCall(500, () => { this.ignoreRotate = false; });
    this.ignoreDrop = true;
    this.time.delayedCall(500, () => { this.ignoreDrop = false; });

    const W = this.scale.width;

    this.animalBodies.forEach(({ rigidBody, image }) => {
      try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
      if (image && image.scene) image.destroy();
    });
    this.animalBodies = [];

    if (this.rapierWorld) { try { this.rapierWorld.free(); } catch(e) {} }
    this.rapierWorld = new this.RAPIER.World({ x: 0, y: 10 });

    const GROUND_H = Math.max(20, 28 * S);
    const groundBody = this.rapierWorld.createRigidBody(
      this.RAPIER.RigidBodyDesc.fixed()
        .setTranslation(toR(this.platformX), toR(this.groundY + GROUND_H / 2))
    );
    this.rapierWorld.createCollider(
      this.RAPIER.ColliderDesc
        .cuboid(toR(this.platformW / 2), toR(GROUND_H / 2))
        .setFriction(0.9).setRestitution(0.0),
      groundBody
    );

    this.score          = 0;
    this.gameOver       = false;
    this.maxTowerHeight = 0;
    this.logicalHeight  = 0;
    this.aimX           = W / 2;
    this.previewAngle   = 0;
    this.canDrop        = false;
    this.currentTurn    = 'player';
    this.lastDropper    = 'player';
    this.playerScore        = 0;
    this.botScore           = 0;
    this.rescueUsed         = false;
    this.lastStableSnapshot = null;
    this.rotationsLeft      = 12;
    this.nextAnimalRevealed = false;
    this.cameras.main.scrollY = 0;

    if (this.botController) this.botController.reset();

    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    if (this.fallenCheckTimer) this.fallenCheckTimer.remove();
    this.fallenCheckTimer = this.time.addEvent({
      delay: 400, callback: () => {
        this.checkFallenAnimals();
        // Play a gentle settling thud when animals are visibly shifting
        if (!this.gameOver && this.animalBodies.length >= 2) {
          const anySliding = this.animalBodies.some(({ rigidBody }) => {
            const v  = rigidBody.linvel();
            const av = rigidBody.angvel();
            return Math.abs(v.x) > 1.8 || Math.abs(v.y) > 2.5 || Math.abs(av) > 1.2;
          });
          if (anySliding) audioManager.soundSlide();
        }
      }, callbackScope: this, loop: true,
    });

    this.time.delayedCall(100, () => {
      this.events.emit('scoreUpdate', 0);
      this.events.emit('heightUpdate', 0);
    });

    this.refillQueue();
    this.currentSpawnY = this.getSpawnY();
    this.spawnNextAnimal();
  }

  restartGame() {
    this.startGame();
    this.events.emit('gameRestarted');
  }

  /* ─────────────────────────── UPDATE ──────────────────────────── */
  update() {
    if (this.clouds) {
      const W = this.scale.width;
      this.clouds.forEach(cloud => {
        cloud.x += cloud.speedX;
        if (cloud.x > W + 120) cloud.x = -120;
      });
    }
    if (!this.rapierWorld) return;

    this.rapierWorld.step();

    this.animalBodies.forEach(({ rigidBody, image }) => {
      if (!image || !image.scene) return;
      const pos = rigidBody.translation();
      const rot = rigidBody.rotation();
      image.setPosition(toPx(pos.x), toPx(pos.y));
      image.setRotation(rot);
    });

    if (!this.gameOver) {
      this.currentSpawnY = this.getSpawnY();

      if (this.previewImage) {
        this.previewImage.x = this.aimX;
        this.previewImage.y = this.currentSpawnY;
      }

      if (this.canDrop) {
        this.aimLine.clear();
        this.aimLine.lineStyle(2 * S, 0xffcc00, 0.4);
        this.aimLine.beginPath();
        this.aimLine.moveTo(this.aimX, this.currentSpawnY + 20 * S);
        this.aimLine.lineTo(this.aimX, this.groundY - 10);
        this.aimLine.strokePath();
      }

      // ── Camera scroll ─────────────────────────────────────────────
      const spawnScreenY = this.currentSpawnY - this.cameras.main.scrollY;
      const scrollThreshold = this.scale.height * 0.25; // start scrolling when spawn is in top 25%

      if (spawnScreenY < scrollThreshold) {
        const targetScrollY = this.currentSpawnY - scrollThreshold;
        this.cameras.main.scrollY = Phaser.Math.Linear(
          this.cameras.main.scrollY,
          targetScrollY,
          0.05  // smoothing — increase for faster, decrease for slower
        );
      }
    }

    if (DEBUG_PHYSICS) this.drawDebug();
  }

  /* ──────────────── ROTATE PREVIEW 30° ────────────────────────── */
  async rotatePreview() {
    if (!this.previewImage) return;

    // Import at method level to avoid circular dependency
    const { getAdsRemoved } = await import('../IAPManager');
    const adsRemoved = await getAdsRemoved();

    // If ads removed, unlimited rotations
    if (!adsRemoved) {
      // Check if rotations left
      if (this.rotationsLeft <= 0) {
        this.events.emit('rotationsExhausted');
        return;
      }
      this.rotationsLeft--;
      // Notify UI of rotation count update
      this.events.emit('rotationCountUpdate', this.rotationsLeft);
    }

    this.previewAngle += ROTATE_STEP;
    if (this.previewAngle >= Math.PI * 2) this.previewAngle = 0;
    this.previewImage.setRotation(this.previewAngle);
  }

  refillRotations() {
    this.rotationsLeft = 12;
    this.events.emit('rotationCountUpdate', this.rotationsLeft);
  }

  getNextAnimalName() {
    if (this.animalQueue.length === 0) this.refillQueue();
    return this.animalQueue[this.animalQueue.length - 1]; // peek at next without popping
  }

  /* ──────────────────── DYNAMIC SPAWN Y ───────────────────────── */
  getSpawnY() {
    const GAP = 60 * S;
    const c = this.currentAnimalName ? cfg(this.currentAnimalName) : null;
    const halfH = c ? c.h / 2 : 40 * S;

    if (this.animalBodies.length === 0) {
      return this.groundY - GAP - halfH;
    }

    const topY = Math.min(...this.animalBodies.map(({ image }) => {
      const ac = cfg(image.texture.key);
      return image.y - (ac ? ac.h / 2 : 40 * S);
    }));

    return topY - GAP - halfH;
  }

  /* ──────────────────── TOWER HEIGHT SCORE ─────────────────────── */
  updateTowerHeight() {
    // no longer used every frame
  }

  calculateFinalHeight() {
    if (this.gameOver) return;
    if (this.animalBodies.length === 0) return;

    const topY = Math.min(...this.animalBodies.map(({ image }) => {
      const c = cfg(image.texture.key);
      return image.y - (c ? c.h / 2 : 0);
    }));

    const heightPx = this.groundY - topY;
    if (heightPx <= 0) return;

    if (heightPx > this.maxTowerHeight) {
      this.maxTowerHeight = heightPx;
      this.logicalHeight = Math.floor(heightPx / this.S);
      this.events.emit('heightUpdate', this.logicalHeight);
    }
  }

  /* ────────────────── CHECK FOR FALLEN ANIMALS ─────────────────── */
  checkFallenAnimals() {
    if (this.gameOver) return;
    const H = this.scale.height;
    const W = this.scale.width;

    for (const { image } of this.animalBodies) {
      if (!image || !image.scene) continue;
      if (image.y > H + 50)                    { this.triggerGameOver(); return; }
      if (image.x < -150 || image.x > W + 150) { this.triggerGameOver(); return; }
    }

    this.animalBodies = this.animalBodies.filter(({ rigidBody, image }) => {
      if (!image || !image.scene) return false;
      if (image.y > H + 300 || image.x < -300 || image.x > W + 300) {
        try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
        image.destroy();
        return false;
      }
      return true;
    });
  }

  /* ──────────────────── EAR-CLIPPING TRIANGULATION ──────────────── */
  static triangulate(verts) {
    const n = verts.length;
    if (n < 3) return [];

    const cross = (o, a, b) =>
      (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const pointInTriangle = (p, a, b, c) => {
      const d1 = cross(p, a, b), d2 = cross(p, b, c), d3 = cross(p, c, a);
      const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0);
      const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0);
      return !(hasNeg && hasPos);
    };

    let area = 0;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += verts[i].x * verts[j].y - verts[j].x * verts[i].y;
    }
    const cwSign = area > 0 ? 1 : -1;

    const idx = [];
    for (let i = 0; i < n; i++) idx.push(i);

    const triangles = [];
    let safetyLimit = n * 3;
    while (idx.length > 3 && safetyLimit-- > 0) {
      let earFound = false;
      for (let i = 0; i < idx.length; i++) {
        const prev = idx[(i - 1 + idx.length) % idx.length];
        const curr = idx[i];
        const next = idx[(i + 1) % idx.length];
        const a = verts[prev], b = verts[curr], c = verts[next];

        if (cross(a, b, c) * cwSign < 0) continue;

        let isEar = true;
        for (let j = 0; j < idx.length; j++) {
          const vi = idx[j];
          if (vi === prev || vi === curr || vi === next) continue;
          if (pointInTriangle(verts[vi], a, b, c)) { isEar = false; break; }
        }
        if (!isEar) continue;

        triangles.push([a, b, c]);
        idx.splice(i, 1);
        earFound = true;
        break;
      }
      if (!earFound) break;
    }
    if (idx.length === 3) {
      triangles.push([verts[idx[0]], verts[idx[1]], verts[idx[2]]]);
    }
    return triangles;
  }

  /* ──────────────────── EXTRACT & SCALE POLYGONS ───────────────── */
  extractScaledPolygons(name) {
    if (!this.shapes || !this.shapes[name]) return [];
    const data = this.shapes[name];
    const c    = cfg(name);
    const isConcave = data.type === 'fromVertices';
    const rawPolygons = [];

    (data.fixtures || []).forEach(fix => {
      if (!fix.vertices) return;
      if (Array.isArray(fix.vertices[0])) {
        if (isConcave) {
          const inner = fix.vertices[0];
          const flat = inner.map(v =>
            Array.isArray(v) ? { x: v[0], y: v[1] } : v
          );
          if (flat.length >= 3) rawPolygons.push(flat);
        } else {
          fix.vertices.forEach(poly => { if (poly.length >= 3) rawPolygons.push(poly); });
        }
      } else if (typeof fix.vertices[0] === 'object' && 'x' in fix.vertices[0]) {
        if (fix.vertices.length >= 3) rawPolygons.push(fix.vertices);
      }
    });

    if (rawPolygons.length === 0) return [];

    const texture = this.textures.get(name).getSourceImage();

    if (isConcave) {
      const allVerts = rawPolygons[0];
      const deduped = allVerts.filter((v, i) => {
        if (i === 0) return true;
        const p = allVerts[i - 1];
        return Math.abs(v.x - p.x) > 0.5 || Math.abs(v.y - p.y) > 0.5;
      });

      const sx = c.w / texture.width;
      const sy = c.h / texture.height;

      const triangles = GameScene.triangulate(deduped);
      const result = [];
      triangles.forEach(tri => {
        const arr = new Float32Array(6);
        tri.forEach((v, i) => {
          arr[i * 2]     = toR(v.x * sx);
          arr[i * 2 + 1] = toR(v.y * sy);
        });
        let area = 0;
        for (let i = 0; i < 3; i++) {
          const x1 = arr[i*2], y1 = arr[i*2+1];
          const x2 = arr[((i+1)%3)*2], y2 = arr[((i+1)%3)*2+1];
          area += (x1 * y2 - x2 * y1);
        }
        if (Math.abs(area) / 2 > 0.002) result.push(arr);
      });
      return result;
    }

    const cx = texture.width  / 2;
    const cy = texture.height / 2;
    const sx = c.w / texture.width;
    const sy = c.h / texture.height;

    const result = [];
    rawPolygons.forEach(poly => {
      const deduped = poly.filter((v, i) => {
        if (i === 0) return true;
        const p = poly[i - 1];
        return Math.abs(v.x - p.x) > 0.5 || Math.abs(v.y - p.y) > 0.5;
      });
      if (deduped.length < 3) return;

      const arr = new Float32Array(deduped.length * 2);
      deduped.forEach((v, i) => {
        arr[i * 2]     = toR((v.x - cx) * sx);
        arr[i * 2 + 1] = toR((v.y - cy) * sy);
      });

      let area = 0;
      const n = arr.length / 2;
      for (let i = 0; i < n; i++) {
        const x1 = arr[i*2], y1 = arr[i*2+1];
        const x2 = arr[((i+1)%n)*2], y2 = arr[((i+1)%n)*2+1];
        area += (x1 * y2 - x2 * y1);
      }
      if (Math.abs(area) / 2 < 0.002) return;
      result.push(arr);
    });

    return result;
  }

  /* ─────────────────── CREATE RAPIER ANIMAL BODY ───────────────── */
  createAnimalBody(name, screenX, screenY, angleRad) {
    const RAPIER   = this.RAPIER;
    const c        = cfg(name);
    const polygons = this.scaledShapes[name] || [];

    const rigidBody = this.rapierWorld.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(toR(screenX), toR(screenY))
        .setRotation(angleRad)
        .setLinearDamping(0.6)
        .setAngularDamping(0.9)
    );

    let added = 0;
    polygons.forEach((verts, i) => {
      try {
        const cd = RAPIER.ColliderDesc.convexHull(verts);
        if (!cd) return;
        cd.setDensity(c.density)
          .setFriction(c.friction)
          .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Average)
          .setRestitution(0.03)
          .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Min);
        this.rapierWorld.createCollider(cd, rigidBody);
        added++;
      } catch (e) {
        console.warn(`Part ${i} of "${name}" failed:`, e.message);
      }
    });

    if (added === 0) {
      this.rapierWorld.createCollider(
        RAPIER.ColliderDesc.cuboid(toR(c.w * 0.45), toR(c.h * 0.45))
          .setDensity(c.density).setFriction(c.friction).setRestitution(0.03),
        rigidBody
      );
    }
    return rigidBody;
  }

  /* ──────────────────── DEBUG RENDERER ─────────────────────────── */
  drawDebug() {
    if (!this.debugGraphics) return;
    this.debugGraphics.clear();
    this.animalBodies.forEach(({ rigidBody, name }) => {
      const bp  = rigidBody.translation();
      const rot = rigidBody.rotation();
      const cos = Math.cos(rot), sin = Math.sin(rot);
      (this.scaledShapes[name] || []).forEach(verts => {
        this.debugGraphics.lineStyle(1.5, 0x00ff00, 0.9);
        this.debugGraphics.beginPath();
        const n = verts.length / 2;
        for (let i = 0; i <= n; i++) {
          const idx = i % n;
          const lx = verts[idx*2], ly = verts[idx*2+1];
          const wx = bp.x + (lx*cos - ly*sin);
          const wy = bp.y + (lx*sin + ly*cos);
          if (i === 0) this.debugGraphics.moveTo(toPx(wx), toPx(wy));
          else         this.debugGraphics.lineTo(toPx(wx), toPx(wy));
        }
        this.debugGraphics.strokePath();
      });
    });
  }

  /* ─────────────────────── ANIMAL QUEUE ────────────────────────── */
  refillQueue() {
    const deck = [...ANIMALS, ...ANIMALS, ...ANIMALS];
    Phaser.Utils.Array.Shuffle(deck);
    this.animalQueue = deck;
  }

  nextAnimalName() {
    if (this.animalQueue.length === 0) this.refillQueue();
    return this.animalQueue.pop();
  }

  /* ─────────────────────── SPAWN PREVIEW ───────────────────────── */
  spawnNextAnimal() {
    this.previewAngle = 0;
    this.currentAnimalName = this.nextAnimalName();
    const c = cfg(this.currentAnimalName);
    if (this.previewImage) this.previewImage.destroy();

    this.previewAngle = 0;

    this.previewImage = this.add.image(this.aimX, this.currentSpawnY, this.currentAnimalName)
      .setDisplaySize(c.w, c.h)
      .setAlpha(0.75)
      .setRotation(0)
      .setDepth(100);

    this.canDrop = true;

    // Reset rotation counter for new animal
    this.rotationsLeft = 12;
    this.nextAnimalRevealed = false;
    this.events.emit('rotationCountUpdate', this.rotationsLeft);
    this.events.emit('nextAnimalStatusUpdate', false);
  }

  /* ───────────────────── DROP ANIMAL ──────────────────────────── */
  dropCurrentAnimal() {
    if (!this.canDrop || this.gameOver) return;
    // In vs_bot mode, only the player can call this
    if (this.gameMode === 'vs_bot' && this.currentTurn !== 'player') return;
    this._executeDrop();
  }

  // Called by BotController — bypasses the player-turn guard
  botDrop() {
    if (!this.canDrop || this.gameOver) return;
    this._executeDrop();
  }

  _executeDrop() {
    this.canDrop = false;
    audioManager.soundDrop();

    // Snapshot the stable tower state BEFORE this drop (used by rescue).
    // In vs_bot mode, only snapshot on the player's turn — we only rescue
    // when the player's drop caused the collapse.
    const isPlayerDrop = this.currentTurn === 'player';
    if (!this.rescueUsed && (this.gameMode === 'solo' || isPlayerDrop)) {
      this._takeSnapshot();
    }

    // Track who dropped this animal (used by triggerGameOver)
    this.lastDropper = this.currentTurn;

    const name   = this.currentAnimalName;
    const c      = cfg(name);
    const x      = this.aimX;
    const spawnY = this.currentSpawnY;
    const angle  = this.previewAngle;

    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    const rigidBody = this.createAnimalBody(name, x, spawnY, angle);
    const image = this.add.image(x, spawnY, name)
      .setDisplaySize(c.w, c.h)
      .setRotation(angle)
      .setDepth(this.animalBodies.length + 10);

    this.animalBodies.push({ rigidBody, image, name });

    // Score is credited only after the animal settles without causing collapse.
    // Pass the dropper identity so settle callbacks know who to credit.
    const dropper = this.currentTurn;
    this.afterDropSettle(dropper);
  }

  afterDropSettle(dropper) {
    // In vs_bot mode, wait for the stack to fully settle before crediting score
    // and flipping the turn. In solo mode, use the fixed 1800ms delay (original
    // behaviour) but still credit score only on settle.
    if (this.gameMode === 'vs_bot') {
      this._waitSettleThenNextTurn(dropper);
    } else {
      this._waitSettleAndScoreSolo();
      this.time.delayedCall(1800, () => {
        if (this.gameOver) return;
        this.currentSpawnY = this.getSpawnY();
        this.spawnNextAnimal();
      });
    }
  }

  // Solo: credit score once stack settles (and tower hasn't collapsed).
  _waitSettleAndScoreSolo() {
    const check = this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        if (this.gameOver) { check.remove(); return; }

        const allSettled = this.animalBodies.every(({ rigidBody }) => {
          const vel = rigidBody.linvel();
          const av  = rigidBody.angvel();
          return Math.abs(vel.x) < 0.5 && Math.abs(vel.y) < 0.5 && Math.abs(av) < 0.5;
        });

        if (allSettled && this.animalBodies.length > 0) {
          check.remove();
          this.score++;
          this.events.emit('scoreUpdate', this.score);
          this.calculateFinalHeight();
          this._awardCoin();
        }
      },
    });
  }

  _waitSettleThenNextTurn(dropper) {
    // Poll every 300ms; once everything has settled, credit score, flip turn, spawn.
    // Enforce a minimum 800ms wait so drops don't feel instant.
    const minWaitDone = { value: false };
    this.time.delayedCall(800, () => { minWaitDone.value = true; });

    const check = this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        if (this.gameOver) { check.remove(); return; }

        const allSettled = this.animalBodies.every(({ rigidBody }) => {
          const vel = rigidBody.linvel();
          const av  = rigidBody.angvel();
          return Math.abs(vel.x) < 0.5 && Math.abs(vel.y) < 0.5 && Math.abs(av) < 0.5;
        });

        if (allSettled && minWaitDone.value && this.animalBodies.length > 0) {
          check.remove();

          this.score++;
          if (dropper === 'player') {
            this.playerScore++;
            this.events.emit('playerScoreUpdate', this.playerScore);
            this._awardCoin();
          } else {
            this.botScore++;
            this.events.emit('botScoreUpdate', this.botScore);
          }

          this.calculateFinalHeight();

          this.currentSpawnY = this.getSpawnY();
          this.currentTurn   = (this.currentTurn === 'player') ? 'bot' : 'player';
          this.events.emit('turnChanged', this.currentTurn);

          this.spawnNextAnimal();

          if (this.currentTurn === 'bot') {
            this.botController.takeTurn();
          }
        }
      },
    });
  }

  /* ───────────────────────── GAME OVER ─────────────────────────── */
  triggerGameOver() {
    if (this.gameOver) return;
    this.gameOver = true;
    audioManager.soundTowerFall();
    this.canDrop  = false;
    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    // Offer rescue if: not already used, snapshot exists, and the PLAYER caused the collapse.
    // Works in both solo (lastDropper is always 'player') and vs_bot (only when player dropped).
    const offerRescue =
      !this.rescueUsed &&
      this.lastStableSnapshot !== null &&
      this.lastDropper === 'player';

    if (offerRescue) {
      this.events.emit('offerRescue');
    } else {
      this.events.emit('gameOver', {
        animals:     this.score,
        towerHeight: this.logicalHeight || 0,
        loser:       this.gameMode === 'vs_bot' ? this.lastDropper : null,
        playerScore: this.playerScore,
        botScore:    this.botScore,
      });
    }
  }

  // Called by UIScene when the player dismisses the rescue offer without watching
  declineRescue() {
    this.events.emit('gameOver', {
      animals:     this.score,
      towerHeight: this.logicalHeight || 0,
      loser:       this.gameMode === 'vs_bot' ? this.lastDropper : null,
      playerScore: this.playerScore,
      botScore:    this.botScore,
    });
  }

  // Restore the tower to the last stable state (called after ad/placeholder)
  rescueGame() {
    if (!this.lastStableSnapshot) return;
    this.rescueUsed = true;

    const snap = this.lastStableSnapshot;
    this.lastStableSnapshot = null;

    // Clear current animal bodies
    this.animalBodies.forEach(({ rigidBody, image }) => {
      try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
      if (image && image.scene) image.destroy();
    });
    this.animalBodies = [];

    // Recreate physics world
    if (this.rapierWorld) { try { this.rapierWorld.free(); } catch(e) {} }
    this.rapierWorld = new this.RAPIER.World({ x: 0, y: 10 });

    // Recreate ground
    const GROUND_H = Math.max(20, 28 * S);
    const groundBody = this.rapierWorld.createRigidBody(
      this.RAPIER.RigidBodyDesc.fixed()
        .setTranslation(toR(this.platformX), toR(this.groundY + GROUND_H / 2))
    );
    this.rapierWorld.createCollider(
      this.RAPIER.ColliderDesc
        .cuboid(toR(this.platformW / 2), toR(GROUND_H / 2))
        .setFriction(0.9).setRestitution(0.0),
      groundBody
    );

    // Restore animals from snapshot (at rest — zero velocity)
    snap.animals.forEach(({ name, x, y, rot }) => {
      const c         = cfg(name);
      const rigidBody = this.createAnimalBody(name, x, y, rot);
      // Zero out any residual velocity
      rigidBody.setLinvel({ x: 0, y: 0 }, true);
      rigidBody.setAngvel(0, true);
      const image = this.add.image(x, y, name)
        .setDisplaySize(c.w, c.h)
        .setRotation(rot)
        .setDepth(this.animalBodies.length + 10);
      this.animalBodies.push({ rigidBody, image, name });
    });

    // Restore state
    this.score          = snap.score;
    this.logicalHeight  = snap.logicalHeight;
    this.maxTowerHeight = snap.maxTowerHeight;
    this.currentSpawnY  = snap.currentSpawnY;
    this.animalQueue    = [...snap.animalQueue];
    this.rotationsLeft  = snap.rotationsLeft || 12;
    this.nextAnimalRevealed = snap.nextAnimalRevealed || false;
    this.gameOver       = false;
    this.cameras.main.scrollY = snap.cameraScrollY;
    // Always give the turn back to the player after rescue
    this.currentTurn    = 'player';
    this.lastDropper    = 'player';

    this.events.emit('rescueComplete', { score: this.score });
    this.events.emit('scoreUpdate', this.score);
    this.events.emit('heightUpdate', this.logicalHeight);
    this.events.emit('rotationCountUpdate', this.rotationsLeft);
    this.events.emit('nextAnimalStatusUpdate', this.nextAnimalRevealed);
    if (this.gameMode === 'vs_bot') {
      this.events.emit('turnChanged', 'player');
    }

    // Give the player their next turn
    this.time.delayedCall(300, () => {
      this.currentSpawnY = this.getSpawnY();
      this.spawnNextAnimal();
    });
  }

  _takeSnapshot() {
    this.lastStableSnapshot = {
      animals: this.animalBodies.map(({ rigidBody, name }) => {
        const pos = rigidBody.translation();
        return {
          name,
          x:   toPx(pos.x),
          y:   toPx(pos.y),
          rot: rigidBody.rotation(),
        };
      }),
      score:          this.score,
      logicalHeight:  this.logicalHeight,
      maxTowerHeight: this.maxTowerHeight,
      currentSpawnY:  this.currentSpawnY,
      animalQueue:    [...this.animalQueue],
      cameraScrollY:  this.cameras.main.scrollY,
      rotationsLeft:  this.rotationsLeft,
      nextAnimalRevealed: this.nextAnimalRevealed,
    };
  }

  showPaused() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    this.pauseContainer = this.add.container(0, 0).setDepth(20);

    const cardW = Math.min(320 * sd, W * 0.85);

    const gap       = 55 * sd;
    const titleY    = H / 2 - gap * 1.5;
    const subtitleY = titleY  + gap;
    const resumeY   = subtitleY + gap;
    const homeY     = resumeY  + gap;

    const cardH       = homeY - titleY + gap * 1.2;
    const cardCentreY = (titleY + homeY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const title = this.add.text(W / 2, titleY, '⏸  PAUSED', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#ffcc00',
    }).setOrigin(0.5);

    const subtitle = this.add.text(W / 2, subtitleY, 'Your score will be lost if you quit here', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      wordWrap: { width: cardW * 0.85 }, align: 'center',
    }).setOrigin(0.5);

    const resumeBtnW = Math.min(240 * sd, W * 0.6);
    const resumeBtnH = 60 * sd;
    const resumeBtn  = this.add.rectangle(W / 2, resumeY, resumeBtnW, resumeBtnH, 0x90dd5c)
      .setInteractive({ useHandCursor: true });
    const resumeText = this.add.text(W / 2, resumeY, '▶  Resume', {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#1a1a2e',
    }).setOrigin(0.5);

    resumeBtn.on('pointerover',  () => resumeBtn.setFillStyle(0xaae87a));
    resumeBtn.on('pointerout',   () => resumeBtn.setFillStyle(0x90dd5c));
    resumeBtn.on('pointerdown',  () => {
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.resumeGame();
    });

    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 50 * sd;
    const homeBtn  = this.add.rectangle(W / 2, homeY, homeBtnW, homeBtnH, 0x4488ff)
      .setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeY, '\u{1F3E0}  Home', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    homeBtn.on('pointerover',  () => homeBtn.setFillStyle(0x66aaff));
    homeBtn.on('pointerout',   () => homeBtn.setFillStyle(0x4488ff));
    homeBtn.on('pointerdown',  () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.start('HomeScene');
    });

    this.pauseContainer.add([
      overlay, card, title, subtitle,
      resumeBtn, resumeText,
      homeBtn, homeBtnText,
    ]);
  }
// ── Coin reward on successful drop ─────────────────
  async _awardCoin() {
    const newBalance = await addCoins(1);
    this.events.emit('coinEarned', newBalance);

    const lastAnimal = this.animalBodies[this.animalBodies.length - 1];
    if (!lastAnimal || !lastAnimal.image || !lastAnimal.image.scene) return;

    const startX = lastAnimal.image.x;
    const startY = lastAnimal.image.y - this.cameras.main.scrollY;
    this._playCoinAnimation(startX, startY);
  }

  _playCoinAnimation(startX, startY) {
    const uiScene = this.scene.get('UIScene');
    if (!uiScene || !uiScene.scene.isActive()) return;

    const dpr = window.devicePixelRatio || 1;
    const sd = this.S * dpr;
    const coinSize = 22 * sd;

    // Target: top-left coin counter position in UIScene
    const targetX = 42 * sd;
    const targetY = 26 * sd;

    // Spawn sparkle particles around the coin origin
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 / 6) * i;
      const dist = 18 * sd;
      const sparkle = uiScene.add.graphics().setDepth(50);
      const sx = startX + Math.cos(angle) * dist;
      const sy = startY + Math.sin(angle) * dist;
      const sparkleSize = (3 + Math.random() * 3) * sd;

      sparkle.fillStyle(0xffdd00, 0.9);
      sparkle.fillCircle(sx, sy, sparkleSize);

      uiScene.tweens.add({
        targets: sparkle,
        x: `+=${Math.cos(angle) * 12 * sd}`,
        y: `+=${Math.sin(angle) * 12 * sd}`,
        alpha: 0,
        duration: 400 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => sparkle.destroy(),
      });
    }

    // Main spinning coin
    const coinGfx = uiScene.add.graphics().setDepth(51);
    this._drawCoin(coinGfx, startX, startY, coinSize);

    // Animate: scale pulse, then fly to counter with spin
    let spinAngle = 0;
    const spinTween = uiScene.tweens.addCounter({
      from: 0, to: 720, duration: 800, ease: 'Linear',
      onUpdate: (t) => {
        spinAngle = t.getValue();
        const scaleX = Math.cos(Phaser.Math.DegToRad(spinAngle));
        coinGfx.setScale(Math.abs(scaleX), 1);
      },
    });

    // Pop out then fly to target
    uiScene.tweens.add({
      targets: coinGfx,
      scaleX: 1.3, scaleY: 1.3,
      duration: 150, yoyo: true, ease: 'Back.easeOut',
      onComplete: () => {
        // Fly to counter
        uiScene.tweens.add({
          targets: coinGfx,
          x: targetX - startX,
          y: targetY - startY,
          scaleX: 0.6, scaleY: 0.6,
          duration: 600,
          ease: 'Power2.easeIn',
          onComplete: () => {
            spinTween.stop();
            coinGfx.destroy();

            // Flash the counter
            if (uiScene.coinCounterText) {
              uiScene.tweens.add({
                targets: uiScene.coinCounterText,
                scaleX: 1.3, scaleY: 1.3,
                duration: 120, yoyo: true, ease: 'Back.easeOut',
              });
            }
          },
        });
      },
    });
  }

  _drawCoin(gfx, x, y, size) {
    // Outer ring
    gfx.fillStyle(0xddaa00, 1);
    gfx.fillCircle(x, y, size);
    // Inner shine
    gfx.fillStyle(0xffdd44, 1);
    gfx.fillCircle(x, y, size * 0.78);
    // Dollar symbol or star
    gfx.fillStyle(0xddaa00, 1);
    gfx.fillCircle(x, y, size * 0.2);
    // Highlight
    gfx.fillStyle(0xffeebb, 0.5);
    gfx.fillCircle(x - size * 0.2, y - size * 0.25, size * 0.25);
  }

  // ── Pause game ─────────────────────────────────
  pauseGame() {
    if (this.gameOver) return;
    this.rapierWorld && this.scene.pause('GameScene');
    this.events.emit('gamePaused');
  }

  resumeGame() {
    this.scene.resume('GameScene');
    this.events.emit('gameResumed');
    this.ignoreDrop = true;
    this.time.delayedCall(500, () => { this.ignoreDrop = false; });
  }
}
